<script setup>
import DOMPurify from 'dompurify';
import { translator } from '@shop/frontend/web/js/services/TranslatorService';
import { dateHelper } from '@shop/frontend/web/js/helpers/dateHelper';
import { helpers } from '@shop/frontend/web/js/helpers';
import { ref, computed } from 'vue';
import { TEAM_ICON_URL } from '@shop/frontend/web/js/constants/event';

const props = defineProps({
  event: {
    type: Object,
    required: true,
  },
  isShort: {
    type: Boolean,
    default: false,
  },
  isPerfomance: {
    type: Boolean,
    default: false,
  },
  isPackage: {
    type: Boolean,
    default: false,
  },
});

const showDescription = ref(false);

const matchesInfoText = computed(() => {
  const matchesCorrectForm = helpers.wordEnding(props.event.eventsCount, [
    translator.t('event', 'match'),
    translator.t('event', 'matches'),
    translator.t('event', 'matches '),
  ]);

  return `${translator.t('event', 'Top')} ${props.event.eventsCount} ${matchesCorrectForm}`;
});

const sanitizedEventDescription = computed(() => DOMPurify.sanitize(props.event.description));
</script>

<template>
  <div>
    <div
      v-if="event.settings && event.settings.isSoon"
      class="i-event-item__soon-block"
    >
      {{
        translator.t(
          "main",
          (event.settings && event.settings.isSoonText) || "Soon"
        )
      }}
    </div>
    <div
      class="i-event-item"
      :class="{
        'i-event-item_short': isShort,
        'i-event-item-info_perfomance' : isPerfomance,
        'i-event-item_soon': event.settings && event.settings.isSoon,
      }"
    >
      <div v-if="event?.settings?.isShowSeason" class="i-event-item__top">
        {{ event.nom.name }}
      </div>
      <div
        class="i-event-item__wrapper"
        :class="{ 'i-event-item__wrapper_perfomance' : isPerfomance }"
      >
        <div
          v-if="!isShort"
          class="i-event-item-info"
          :class="{ 'i-event-item-info_perfomance' : isPerfomance }"
        >
          <slot name="info">
            <div
              v-if="event.eventsCount && !isPackage"
              class="i-event-item-info__matches"
            >
              {{ matchesInfoText }}
            </div>

            <div
              v-if="event.settings.isShowDate && !isPackage"
              class="d-inline-flex align-items-center i-event-item-info__text text-primary mb-2"
            >
              <svg
                class="icon i-icon_stroke-color i-icon_sm mr-2"
                width="15"
                height="15"
              >
                <use xlink:href="#calendar" />
              </svg>
              <span class="text-capitalize">
                {{ event.startTimestampStr }}
              </span>
            </div>

            <div
              v-if="event.settings.isShowUnit"
              class="d-inline-flex align-items-center i-event-item-info__text"
            >
              <svg
                class="icon i-icon_stroke-color i-icon_sm mr-2"
                width="15"
                height="15"
              >
                <use xlink:href="#location" />
              </svg>
              <span>
                {{ event.unitName }}
              </span>
            </div>
          </slot>
        </div>
        <slot name="name">
          <component
            :is="!event.settings.isSoon ? 'a' : 'div'"
            v-if="event.participants && event.participants.singular"
            v-bind="!event.settings.isSoon ? { href: event.pageLink } : {}"
            class="i-event-item-logo-name"
          >
            <img
              class="i-event-item__logo"
              :src="event.participants.singular.imageUrl || TEAM_ICON_URL"
              alt=""
              width="50"
              height="50"
            >
            <span class="i-event-item__name">
              {{ event.participants.singular.name }}
            </span>
          </component>
          <component
            :is="!event.settings.isSoon ? 'a' : 'div'"
            v-else-if="isPackage"
            v-bind="!event.settings.isSoon ? { href: event.pageLink } : {}"
            class="i-event-item-logo-name"
          >
            <img
              class="i-event-item__logo"
              :src="event.packageEvents[0]?.participants.team.homeTeam.imageUrl || TEAM_ICON_URL"
              alt=""
              width="50"
              height="50"
            >
            <div class="i-event-item__package-name-wrapper">
              <span class="i-event-item__name_package">
                {{ event.name }}
              </span>
              <span class="i-event-item__nom-name" v-if="event?.nomName">
                {{ event.nomName }}
              </span>
            </div>
          </component>
        </slot>
        <div class="i-event-item-actions">
          <slot name="actions">
            <button
              v-if="event.description"
              type="button"
              class="i-btn i-btn_outlined"
              @click="showDescription = !showDescription"
            >
              {{
                showDescription
                  ? translator.t("main", "Hide")
                  : translator.t("main", "More details")
              }}
            </button>

            <a
              v-if="!(event.settings && event.settings.isSoon)"
              :href="event.pageLink"
              :title="translator.t('main', 'Go to the event page')"
              class="i-btn js-hover-out"
            >
              <svg
                width="20"
                height="20"
                class="icon icon_cart"
                aria-hidden="true"
                focusable="false"
              >
                <use xlink:href="#cart" />
              </svg>
              <span class="sr-only">
                {{ translator.t("main", "Go to the event page") }}
              </span>
            </a>
          </slot>
        </div>
      </div>
      <div
        v-if="event.description && showDescription"
        class="i-event-item-expand"
        :class="{ 'd-block': showDescription }"
        v-html="sanitizedEventDescription"
      />
    </div>
  </div>
</template>

<style lang="scss">
.i-event-item {
  border-radius: 20px;
  padding: 0;

  &__top {
    background: #E2EAF8;
    border-radius: $border-radius $border-radius 0 0;
    padding: 8px 0;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__wrapper {
    padding: 25px 30px;
  }

  &_soon {
    border-top: none;
    border-radius: 0 0 20px 20px;
  }

  &__soon-block {
    text-align: center;
    border-radius: 15px 15px 0 0;
    padding: 3px 0;
    width: 100%;
    background-color: #FBDAC4;
    font-weight: 500;
    font-size: 16px;
  }

  &__package-name-wrapper {
    display: flex;
    flex-direction: column;
    margin-left: 12px;
  }

  &__nom-name {
    font-size: 14px;
    color: $grey-text;
  }

  &__name_package {
    color: $dark-text;
    font-size: 18px;
    font-weight: 700;
  }

  @media (max-width: $screen-xs) {
    &__package-name-wrapper {
      gap: 8px;
      align-items: center;
      text-align: center;
    }
  }
}
</style>
